/**
 * Databases Folder Expansion - Handles loading databases when the folder is expanded.
 *
 * Slice 5b: now reads the full `{ state, databases, error }` shape from
 * `getDatabases` rather than relying on a thrown error. The three states
 * (`'ok'` / `'empty'` / `'error'`) each render a distinct tree node so the
 * Object Explorer matches the affordance offered by the Table Explorer
 * dropdown — same call, same UI.
 */

import { isCancelledError } from '@tanstack/react-query';

import { sanitizeConnectionErrorMessage } from './connectionErrorUtils';
import { isConnectionLive } from './databaseListSharing';
import {
  DatabaseExplorerNode,
  FolderTreeNode,
  MessageTreeNode,
  createDatabaseNode,
  createSystemDatabasesFolderNode,
  createDatabaseObjectTypeFolders,
  isSystemDatabase,
} from '../../explorer/models/DatabaseTreeModels';
import { DatabaseEngineId } from '../../../core/engines/engineTypes';
import type { GetDatabasesResult } from './databasesService';
import { useConnectionStore } from '../../connections/store';
import { getLimitedDatabase } from '../../connections/utils/connectionDatabaseLimit';
import { isKustoProxyConnection } from '../../connections/services/kustoProxyConnection';

export interface LoadDatabasesOptions {
  node: FolderTreeNode;
  connectionId: string;
  /** Engine type for creating engine-specific security folders */
  engine?: DatabaseEngineId;
  isPreloading: (connectionId: string) => boolean;
  /**
   * Returns the full result (including `state` + structured `error`). Slice 5b
   * stopped throwing on listing failure so consumers — tree, dropdown, dialogs
   * — can render consistent UI from the same shape.
   */
  fetchDatabasesForConnection: (connectionId: string) => Promise<GetDatabasesResult>;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
  /** Called when a fetch error occurs - can be used to detect connection loss */
  onFetchError?: (connectionId: string, errorMessage: string) => void;
  /** Called after databases are successfully loaded - can be used to trigger object preloading */
  onDatabasesLoaded?: (connectionId: string, databases: string[]) => void;
  /**
   * "The connection this listing was started for is gone." Captured BEFORE the
   * listing starts and checked when it returns: a held listing that resolves
   * after a disconnect / reconnect would otherwise rebuild the CURRENT tree
   * from an obsolete list, consume the live connect's bookkeeping through the
   * callbacks below, and report its readiness. Nothing after the fetch runs
   * when this says stale — not the tree write, not either callback.
   */
  isStale?: () => boolean;
  /**
   * Called when the user clicks the *Show details* affordance on the failure
   * tile. Slice 3 will wire this to the diagnostics dialog; for Slice 5b we
   * accept the callback so the integration point exists end-to-end.
   */
  onShowDiagnostics?: (
    connectionId: string,
    trigger: 'list_failure' | 'list_empty'
  ) => void;
  /** Force reload even if children exist (for refresh) */
  forceReload?: boolean;
}

type UnverifiedKind = 'config' | 'known';

/**
 * B3 — the trimmed database a connection is scoped to, via the shared
 * {@link getLimitedDatabase} rule. Read lazily from the connection store
 * (saved `connections` win, then live `activeConnections`). UI scoping only —
 * never a query gate.
 */
function getLimitedConnectionDatabase(connectionId: string): string | undefined {
  try {
    const state = useConnectionStore.getState();
    const connection =
      state.connections?.find((c) => c.id === connectionId) ??
      state.activeConnections?.find((c) => c.id === connectionId)?.connection;
    return getLimitedDatabase(connection);
  } catch {
    return undefined;
  }
}

function buildDatabaseChildren(
  connectionId: string,
  databases: string[],
  engine?: DatabaseEngineId,
  /**
   * Slice 5: when populated, each name in this map renders the `?` indicator
   * with the matching kind. Absence ⇒ authoritative listing, no indicator.
   */
  unverifiedByName?: Map<string, UnverifiedKind>
): DatabaseExplorerNode[] {
  // B3: a limited connection always renders its (single) scoped database as a
  // top-level node with NO "System Databases" folder — even when the limited
  // database is itself a system-named db (e.g. `master`). `getDatabases` has
  // already scoped the list, so here we only suppress the system grouping.
  const isLimited = getLimitedConnectionDatabase(connectionId) !== undefined;
  const systemDatabases = isLimited ? [] : databases.filter(isSystemDatabase);
  const userDatabases = isLimited ? databases : databases.filter((db) => !isSystemDatabase(db));

  const databaseNodes = userDatabases.map((dbName) => {
    const unverified = unverifiedByName?.get(dbName);
    const node = createDatabaseNode(connectionId, dbName, {
      engine,
      ...(unverified ? { unverified } : {}),
    });
    node.children = createDatabaseObjectTypeFolders(connectionId, dbName, engine, isKustoProxyConnection(connectionId));
    return node;
  });

  const children: DatabaseExplorerNode[] = [];

  if (systemDatabases.length > 0) {
    const systemFolder = createSystemDatabasesFolderNode(connectionId);
    systemFolder.children = systemDatabases.map((dbName) => {
      const unverified = unverifiedByName?.get(dbName);
      const node = createDatabaseNode(connectionId, dbName, {
        engine,
        ...(unverified ? { unverified } : {}),
      });
      node.children = createDatabaseObjectTypeFolders(connectionId, dbName, engine, isKustoProxyConnection(connectionId));
      return node;
    });
    children.push(systemFolder);
  }

  children.push(...databaseNodes);
  return children;
}

/**
 * Slice 5: build the smart-fallback list when listing fails.
 *
 * Returns `[config.database, ...knownDatabases]` (de-duplicated) so the user
 * sees databases they're verifiably able to access — even when SQL itself
 * cannot enumerate the user's full DB list (e.g. contained-user shapes
 * on Azure SQL). Returns an empty array when neither is available; the
 * caller falls back to the legacy hardcoded system-DB list in that case.
 */
function buildSmartFallbackList(
  connectionId: string,
): { entries: string[]; kindByName: Map<string, UnverifiedKind> } {
  const empty = { entries: [] as string[], kindByName: new Map<string, UnverifiedKind>() };
  try {
    const connection = useConnectionStore
      .getState()
      .connections.find((c) => c.id === connectionId);
    if (!connection) return empty;
    const configDb = connection.database?.trim() || undefined;
    const known = connection.knownDatabases ?? [];
    const seen = new Set<string>();
    const entries: string[] = [];
    const kindByName = new Map<string, UnverifiedKind>();
    if (configDb) {
      seen.add(configDb.toLowerCase());
      entries.push(configDb);
      kindByName.set(configDb, 'config');
    }
    // B3: a limited connection's fallback is EXACTLY its configured database —
    // never the wider `knownDatabases`. (Inert when the flag is set but no
    // database is configured — then fall through to the normal known list.)
    if (getLimitedDatabase(connection)) {
      return { entries, kindByName };
    }
    for (const name of known) {
      const trimmed = name?.trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      entries.push(trimmed);
      kindByName.set(trimmed, 'known');
    }
    return { entries, kindByName };
  } catch {
    return empty;
  }
}

function buildShowDetailsNode(
  parentNodeId: string,
  connectionId: string,
  trigger: 'list_failure' | 'list_empty',
  primary: string,
  hint: string
): { error: MessageTreeNode; retry: MessageTreeNode; details: MessageTreeNode } {
  const error: MessageTreeNode = {
    id: `error-${connectionId}-databases`,
    parentId: parentNodeId,
    name: primary,
    type: 'error',
    canHaveChildren: false,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      error: primary,
    },
  };

  // Transient listing failures (VPN blip, sleepy server) previously forced
  // users to find the folder's context-menu Refresh — an inline Retry tile
  // re-runs the listing in place via the same force-reload path.
  const retry: MessageTreeNode = {
    id: `retry-${connectionId}-databases`,
    parentId: parentNodeId,
    name: 'Retry',
    type: 'message',
    canHaveChildren: false,
    children: [],
    isExpanded: false,
    icon: 'hint',
    metadata: {
      connectionId,
      action: 'retry-list-databases',
    },
  };

  const details: MessageTreeNode = {
    id: `show-details-${connectionId}-databases`,
    parentId: parentNodeId,
    name: hint,
    type: 'message',
    canHaveChildren: false,
    children: [],
    isExpanded: false,
    icon: 'hint',
    metadata: {
      connectionId,
      action: 'show-connection-diagnostics',
      diagnosticsTrigger: trigger,
    },
  };

  return { error, retry, details };
}

/**
 * Sentinel for "this listing must paint NOTHING at all" — see
 * {@link listDatabasesThroughCancellation}.
 */
const DROP_LISTING = Symbol('drop-listing');

/** The failure shape the tree renders when a listing throws. */
function listingErrorResult(caught: unknown): GetDatabasesResult {
  const rawMessage = caught instanceof Error ? caught.message : 'Failed to load databases';
  return { state: 'error', databases: [], error: { message: rawMessage }, durationMs: 0 };
}

/**
 * "The connect this listing belongs to is over." Asked only about a CANCELLED
 * listing, and it needs BOTH halves — neither is sufficient on its own:
 *
 * - `isStale()` is the Explorer's generation fence, advanced from a React effect,
 *   so it is still `false` for a cancellation raised synchronously inside
 *   zustand's `set()`;
 * - liveness is read from the store, which zustand has ALREADY updated by the
 *   time such a subscriber runs — but it says nothing about a generation the
 *   Explorer ended for its own reasons (a Force Reconnect in place keeps the id
 *   live).
 *
 * An unreadable store answers "over": painting nothing is the safe side of this
 * decision, and it is what this catch did before the two causes were split.
 */
function listingConnectEnded(connectionId: string, isStale?: () => boolean): boolean {
  if (isStale?.()) return true;
  try {
    return !isConnectionLive(useConnectionStore.getState().activeConnections, connectionId);
  } catch {
    return true;
  }
}

/**
 * Run the listing, and decide what a CANCELLATION means — because it has two
 * causes that need opposite handling, and treating them alike breaks one of them.
 *
 * A cancellation is never a listing failure: `fetchDatabasesForConnection` goes
 * through `fetchQuery`, so anything that cancels or destroys this connection's
 * query key rejects here with a `CancelledError`. Building the failure tile from
 * one paints "Couldn't list databases: CancelledError" plus unverified fallback
 * rows — wrong in both of the cases below, which is why neither of them reaches
 * that path.
 *
 * 1. THE CONNECT ENDED (`connectionListingLifecycle` / `endConnectionGeneration`
 *    cancelling at a connect boundary — a disconnect while the on-connect listing
 *    is in flight). Nothing is painted: the tree write, both callbacks and the
 *    fallback rows would all land under a node that is already disconnected. The
 *    boundary that cancelled the listing owns the repaint, and the
 *    "Loading databases…" placeholder left behind is what the disconnect handler
 *    collapses (`isDatabasesFolderUnloaded`).
 *
 * 2. THE CONNECT IS STILL LIVE, and this listing is merely OBSOLETE. Saving a
 *    connection edit that changes what `getDatabases` returns removes the shared
 *    entry (`updateConnection` → `connectionEditChangesDatabaseListing` →
 *    `removeQueries`) so no picker can paint the pre-edit list, and destroying a
 *    query cancels its in-flight promise. A `limitToDatabase`-only edit is not a
 *    dial field, so `connectionRequiresPoolReset` does not fire: the connection
 *    stays connected and NO disconnect / reconnect effect ever comes to clear or
 *    replace the placeholder. Returning here would leave the folder pulsing
 *    "Loading databases…" until the user collapsed and re-expanded it — on the
 *    very feature whose point is not showing that. So re-list: a fresh listing is
 *    what the edit asked for anyway.
 *
 * Why the re-list is safe:
 * - it cannot SPIN. There is no loop and no recursion here: exactly one re-list,
 *   and a second cancellation ends either dropped (case 1) or on the failure tile.
 * - it cannot STOMP a successor. `fetchQuery` keys on the shared listing key, so a
 *   re-list joins a successor already in flight for this connection (or is served
 *   its fresh result) instead of racing it.
 * - it cannot outlive its connect. The caller's `isStale?.()` fence still runs on
 *   the result, so a re-list that lands after the connect ended paints nothing.
 */
async function listDatabasesThroughCancellation(
  connectionId: string,
  fetchDatabasesForConnection: (connectionId: string) => Promise<GetDatabasesResult>,
  isStale: (() => boolean) | undefined,
): Promise<GetDatabasesResult | typeof DROP_LISTING> {
  try {
    return await fetchDatabasesForConnection(connectionId);
  } catch (caught) {
    // Defensive: Slice 5b's `fetchDatabasesForConnection` should not throw at
    // all, but if it does fall back to the same *Show details* affordance.
    if (!isCancelledError(caught)) return listingErrorResult(caught);
    if (listingConnectEnded(connectionId, isStale)) return DROP_LISTING;
    try {
      return await fetchDatabasesForConnection(connectionId);
    } catch (retryCaught) {
      if (isCancelledError(retryCaught) && listingConnectEnded(connectionId, isStale)) {
        return DROP_LISTING;
      }
      return listingErrorResult(retryCaught);
    }
  }
}

export async function loadDatabasesFolder({
  node,
  connectionId,
  engine,
  isPreloading,
  fetchDatabasesForConnection,
  updateNode,
  onFetchError,
  onDatabasesLoaded,
  onShowDiagnostics,
  isStale,
  forceReload,
}: LoadDatabasesOptions) {
  // A background preload in flight normally means the listing already ran —
  // re-running it would stomp on the tree the preload is filling in. An
  // EXPLICIT refresh / Retry (`forceReload`) is the exception: the user asked
  // for a fresh listing, and a stuck `isPreloading` must never make the Retry
  // tile inert.
  if (!forceReload && isPreloading(connectionId)) {
    return;
  }

  if (!forceReload && node.children && node.children.length > 0) {
    return;
  }

  updateNode(node.id, {
    children: [
      {
        id: `loading-${connectionId}-databases`,
        parentId: node.id,
        name: 'Loading databases...',
        type: 'message',
        canHaveChildren: false,
        children: [],
        isExpanded: false,
        icon: 'loading',
      },
    ],
    isLoading: true,
  });

  const listing = await listDatabasesThroughCancellation(
    connectionId,
    fetchDatabasesForConnection,
    isStale,
  );
  // Cancelled at a connect boundary — the boundary repaints, not this listing.
  if (listing === DROP_LISTING) return;
  const result = listing;
  // The listing belonged to a connect that is over — drop it whole.
  if (isStale?.()) return;

  // Tree click handler resolves diagnosticsTrigger via the node metadata,
  // not via this callback ref, so the parameter is informational only.
  void onShowDiagnostics;

  if (result.state === 'ok') {
    const children = buildDatabaseChildren(connectionId, result.databases, engine);
    updateNode(node.id, { children, isLoading: false });
    // The tree (above) still renders every database. Background object
    // preload, however, only targets databases the login can actually open
    // — `accessibleDatabases` (MSSQL `HAS_DBACCESS`) when present, else the
    // full list (non-MSSQL / legacy result). This stops the per-database ×
    // per-object-type fan-out of doomed pool opens that flooded
    // `feature:db_pool_open_failed` on shared/partial-grant servers.
    // Unfiltered: `planPreloadOrder` needs to see the whole accessible list to
    // pick the wave leader, which for a connection with no configured database
    // IS a system database (`master` / `postgres` / `mysql` — wherever the
    // session actually sits). It drops the system databases from the rest.
    onDatabasesLoaded?.(connectionId, result.accessibleDatabases ?? result.databases);
    return;
  }

  if (result.state === 'empty') {
    const { error, retry, details } = buildShowDetailsNode(
      node.id,
      connectionId,
      'list_empty',
      'Server returned no databases.',
      'Show details'
    );
    const smartFallback = buildSmartFallbackList(connectionId);
    const fallbackChildren = buildDatabaseChildren(
      connectionId,
      smartFallback.entries,
      engine,
      smartFallback.kindByName
    );
    updateNode(node.id, {
      children: [error, retry, details, ...fallbackChildren],
      isLoading: false,
    });
    return;
  }

  // state === 'error'
  const errorMessage = result.error?.message ?? 'Failed to load databases';
  onFetchError?.(connectionId, errorMessage);
  const { error, retry, details } = buildShowDetailsNode(
    node.id,
    connectionId,
    'list_failure',
    `Couldn't list databases: ${sanitizeConnectionErrorMessage(errorMessage)}`,
    'Show details'
  );
  const smartFallback = buildSmartFallbackList(connectionId);
  const fallbackChildren = buildDatabaseChildren(
    connectionId,
    smartFallback.entries,
    engine,
    smartFallback.kindByName
  );
  updateNode(node.id, {
    children: [error, retry, details, ...fallbackChildren],
    isLoading: false,
  });
}
